/* Target vs actual, with efficiency on a second axis.
 *
 * Target is an outline bar and actual a narrower solid one drawn inside it,
 * so the gap between plan and reality is the visible white space rather
 * than something the eye has to compute from two side-by-side heights.
 */

import { GridLines, Plot, PLOT_W } from './Plot'
import { bandValues, effFill, niceMax, polyline, slotCentre, slotWidth, yScale } from '../../lib/scale'

const EFF_CEILING = 120   // efficiency axis top, in per cent

export function TargetActual({
  height = 280, target, actual, efficiency, benchmark, labelY, xLabels,
  onSelect, selected, valueLabel, tooltip,
}: {
  height?: number
  target: number[]
  actual: number[]
  efficiency: number[]
  benchmark: number
  labelY: (v: number) => string
  xLabels?: React.ReactNode
  onSelect?: (i: number) => void
  selected?: number | null
  /** Per-column value shown inside the plot. Omit for no data labels. */
  valueLabel?: (i: number) => string
  /** Hover readout for column `i`. */
  tooltip?: (i: number) => React.ReactNode
}) {
  const n = target.length
  const max = niceMax(Math.max(...target, ...actual, 1))
  const y = yScale(0, max, height)
  const yEff = yScale(0, EFF_CEILING, height)
  const slot = slotWidth(n)

  const gridYs = bandValues(0, max, 4).map(y)
  const effPts = efficiency.map((e, i) => [slotCentre(i, n), yEff(Math.min(e, EFF_CEILING))] as [number, number])

  /* Data labels sit in the headroom above the tallest bar. The axis is
   * rounded up past the data, so that band is empty — and it clears the
   * efficiency line, which tracks just below the bar tops. */
  const tallest = Math.min(...target.map(y), ...actual.map(y))
  const labels = valueLabel ? (
    <div style={{
      position: 'absolute', left: 0, right: 0, top: 0,
      height: Math.max(0, tallest - 4),
      display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr',
      alignItems: 'end',
    }}>
      {target.map((_, i) => (
        <div key={i} className="num" style={{
          textAlign: 'center', fontSize: 10, fontWeight: 600, lineHeight: 1.25,
          color: selected === i ? 'var(--color-accent-900)' : 'var(--color-accent-800)',
        }}>
          <div>{valueLabel(i)}</div>
          <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--color-neutral-600)' }}>
            {efficiency[i].toFixed(0)}%
          </div>
        </div>
      ))}
    </div>
  ) : undefined

  return (
    <Plot
      height={height}
      yTitle="Output kg"
      yLabels={bandValues(0, max, 4).map(labelY)}
      yTitleRight="Efficiency %"
      yLabelsRight={bandValues(0, EFF_CEILING, 4).map((v) => Math.round(v) + '%')}
      xLabels={xLabels}
      overlay={labels}
      bands={n}
      tooltip={tooltip}
    >
      <GridLines ys={gridYs} />

      {/* the benchmark the mill measures itself against */}
      <line x1="0" y1={yEff(benchmark)} x2={PLOT_W} y2={yEff(benchmark)}
        stroke="var(--color-accent-900)" strokeWidth="1" strokeDasharray="4 4"
        opacity="0.55" vectorEffect="non-scaling-stroke" />

      {target.map((t, i) => {
        const cx = slotCentre(i, n)
        const tw = slot * 0.68
        const aw = slot * 0.4
        const ty = y(t)
        const ay = y(actual[i])
        const isSel = selected === i
        return (
          <g key={i} onClick={onSelect ? () => onSelect(i) : undefined}
            style={onSelect ? { cursor: 'pointer' } : undefined}>
            {/* full-height hit target — bars alone are fiddly to click.
                The selected column is tinted rather than filled so it reads
                as a highlight behind the bars, not as another series. */}
            {onSelect ? (
              <rect x={slot * i} y="0" width={slot} height={height}
                fill={isSel ? 'var(--color-accent-600)' : 'transparent'}
                opacity={isSel ? 0.16 : 1} />
            ) : null}
            <rect x={cx - tw / 2} y={ty} width={tw} height={height - ty}
              fill="none" stroke="var(--color-accent-500)" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
            <rect x={cx - aw / 2} y={ay} width={aw} height={height - ay}
              fill={effFill(efficiency[i], benchmark)} />
          </g>
        )
      })}

      <polyline points={polyline(effPts)} fill="none" stroke="var(--color-accent-900)"
        strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      {effPts.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="3" fill="var(--color-accent-900)" />
      ))}
    </Plot>
  )
}
